import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../../../../environments/environment';
import { GetCatalogsRequest } from '../models/get-catalogs.request';
import { GetCatalogsResponse } from '../models/get-catalogs.response';
import { catchError, map, Observable, throwError } from 'rxjs';
import { convertKeysToCamelCase } from '../../../../../shared/utilities/json-key-transform';
import { Catalog } from '../models/catalog.model';
import { CreateCatalogRequest } from '../models/create-catalog.request';
import { CreateCatalogResponse } from '../models/create-catalog.response';
import { DataStore } from '../../data-lakes-tab/models/data-store.model';
import { GetUnassignedDataStoresRequest } from '../models/get-unassigned-data-stores.request';
import { GetUnassignedDataStoresResponse } from '../models/get-unassigned-data-stores.response';

@Injectable({
  providedIn: 'root'
})
export class CatalogService {
  private http = inject(HttpClient);
  private apiUrl = environment.catalogApi.serviceBaseUrl;

  private lastRequestedTenantId = signal<string | null>(null);

  catalogs = signal<Catalog[]>([]);

  isCatalogLoading = signal<boolean>(false);
  catalogErrorMessage = signal<string>("");

  getCatalogList(request: GetCatalogsRequest): void {
    this.isCatalogLoading.set(true);
    this.catalogs.set([]);
    this.lastRequestedTenantId.set(request.tenantId);

    this.http.post<GetCatalogsResponse>(this.apiUrl + "/GetCatalogs", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as GetCatalogsResponse;
        return camelCase.catalogs;
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.status + ": " + error.statusText);
      })
    ).subscribe({
      next: (catalog) => {
        if (request.tenantId === this.lastRequestedTenantId()) {
          this.catalogs.set(catalog);
          this.isCatalogLoading.set(false);
        }
      },
      error: (error: string) => {
        if (request.tenantId === this.lastRequestedTenantId()) {
          this.catalogs.set([]);
          this.catalogErrorMessage.set("Failed to get catalogs. Please try again later. ("+error+")");
          this.isCatalogLoading.set(false);
        }
      }
    });
  }

  getUnassignedDataLakes(request: GetUnassignedDataStoresRequest): Observable<DataStore[]> {
    return this.http.post<GetUnassignedDataStoresResponse>(this.apiUrl + "/GetUnassignedDataLakes", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as GetUnassignedDataStoresResponse;
        if (camelCase.error !== null) {
          throw new Error(camelCase.error || 'An unexpected error occurred');
        }
        return camelCase.dataStores;
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.status + ": " + error.statusText);
      })
    );
  }

  getUnassignedLandingZones(request: GetUnassignedDataStoresRequest): Observable<DataStore[]> {
    return this.http.post<GetUnassignedDataStoresResponse>(this.apiUrl + "/GetUnassignedLandingZones", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as GetUnassignedDataStoresResponse;
        if (camelCase.error !== null) {
          throw new Error(camelCase.error || 'An unexpected error occurred');
        }
        return camelCase.dataStores;
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.status + ": " + error.statusText);
      })
    );
  }

  createCatalog(request: CreateCatalogRequest): Observable<Catalog> {
    return this.http.post<CreateCatalogResponse>(this.apiUrl + "/CreateCatalog", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as CreateCatalogResponse;
        if (camelCase.error !== null) {
          throw new Error(camelCase.error || 'An unexpected error occurred');
        }
        return camelCase.catalog;
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.error.error);
      })
    );
  }
}
